import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { api } from "../api/client";

const CAT_LABELS = {
  actualite: "Actualité", conseil_municipal: "Conseil municipal",
  arrete: "Arrêté", manifestation: "Manifestation",
  vie_scolaire: "Vie scolaire", information: "Information",
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch { return iso; }
}

const UPLOADS = "/uploads/actualites/";
const DOCUMENTS_BASE = "/uploads/documents/";

export default function ActualiteDetail() {
  const { slug } = useParams();
  const [actu, setActu] = useState(null);
  const [error, setError] = useState("");
  const [lightbox, setLightbox] = useState(null);   // url image agrandie
  const [pdfOpen, setPdfOpen] = useState(null);      // document PDF previsualise

  useEffect(() => {
    api.get(`/actualites/${slug}`)
      .then(setActu)
      .catch(() => setError("Cette actualité est introuvable ou n'est plus disponible."));
  }, [slug]);

  // Fermer la lightbox avec Echap
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { setLightbox(null); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (error) {
    return (
      <div className="container">
        <Link to="/actualites" className="back-link">← Retour aux actualités</Link>
        <div className="error">{error}</div>
      </div>
    );
  }

  if (!actu) return <div className="container"><p className="muted">Chargement…</p></div>;

  const secondaires = (actu.photos || []).filter((p) => p.filename !== actu.image_principale);
  const documents = actu.documents || [];

  return (
    <div className="container">
      <Link to="/actualites" className="back-link">← Retour aux actualités</Link>

      <span className={`badge badge-${actu.categorie}`}>
        {CAT_LABELS[actu.categorie] || actu.categorie}
      </span>
      <h1>{actu.titre}</h1>
      <p className="muted mb-3">{formatDate(actu.date_publication)}</p>

      {actu.image_principale && (
        <img className="actu-hero-img mb-3"
             src={UPLOADS + actu.image_principale} alt={actu.titre}
             onClick={() => setLightbox(UPLOADS + actu.image_principale)} />
      )}

      {actu.chapeau && <p style={{ fontSize: "1.1rem", fontWeight: 500 }} className="mb-2">{actu.chapeau}</p>}

      <div className="article-content"
           dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(actu.contenu_html || "") }} />

      {actu.lien_rubrique && (
        <div className="actu-rubrique-cta">
          <Link to={`/rubrique/${actu.lien_rubrique}`} className="rubrique-cta-btn">
            En savoir plus sur cette rubrique →
          </Link>
        </div>
      )}

      {/* Galerie photos : clic pour agrandir */}
      {secondaires.length > 0 && (
        <div className="actu-gallery">
          {secondaires.map((p) => (
            <figure key={p.id} onClick={() => setLightbox(UPLOADS + p.filename)}>
              <img src={UPLOADS + p.filename} alt={p.legende || actu.titre} />
              {p.legende && <figcaption className="muted">{p.legende}</figcaption>}
            </figure>
          ))}
        </div>
      )}

      {/* Documents PDF */}
      {documents.length > 0 && (
        <div className="actu-docs">
          <h3>Documents à télécharger</h3>
          <ul className="actu-doc-list">
            {documents.map((d) => (
              <li key={d.id}>
                <span className="actu-doc-name">📄 {d.titre || d.filename}</span>
                <span className="actu-doc-actions">
                  <button className="secondary small"
                          onClick={() => setPdfOpen(pdfOpen && pdfOpen.id === d.id ? null : d)}>
                    {pdfOpen && pdfOpen.id === d.id ? "Masquer" : "Prévisualiser"}
                  </button>
                  <a className="secondary small" href={DOCUMENTS_BASE + d.filename}
                     target="_blank" rel="noopener noreferrer">Nouvel onglet ↗</a>
                </span>
                {pdfOpen && pdfOpen.id === d.id && (
                  <iframe className="actu-doc-iframe" title={d.titre || d.filename}
                          src={DOCUMENTS_BASE + d.filename} />
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Lightbox image plein ecran */}
      {lightbox && (
        <div className="lightbox-overlay" onClick={() => setLightbox(null)}>
          <button className="lightbox-close" onClick={() => setLightbox(null)}>×</button>
          <img className="lightbox-img" src={lightbox} alt=""
               onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
